import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { View } from 'react-native';
import Animated, { runOnJS, useAnimatedScrollHandler, useDerivedValue, useSharedValue, } from 'react-native-reanimated';
import { NavigationHeader, rem } from 'design-system';
import SelectorControllableReanimated from 'design-system/SelectorControllableReanimated';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { selectDefaultCurrency } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { triggerSelectorPress } from 'utils/hapticFeedback';
import { selectIsExtraMembersFeatureAvailable } from 'reducers/selectors/sharedPlans';
import { getCreditReferrals } from '../actions';
import ReferralsStatusList from './ReferralsStatusList';
import { isWeb } from '../../../constants';
const ReferralsList = ({ referrals }) => {
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(getCreditReferrals());
    }, []);
    const { COMPLETED, SIGNED_UP } = useMemo(() => {
        const { currency } = referrals;
        return (referrals.referrals || []).reduce((prev, curr) => ({
            ...prev,
            [curr.status]: {
                data: [...prev[curr.status].data, curr],
                total: curr.getCreditAmount.currency === currency
                    ? prev[curr.status].total + curr.getCreditAmount.value
                    : prev[curr.status].total,
            },
        }), {
            COMPLETED: {
                data: [],
                total: 0,
            },
            SIGNED_UP: {
                data: [],
                total: 0,
            },
        });
    }, [referrals]);
    const styles = useStyles(styleSet);
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const { width, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const pendingScrollY = useSharedValue(0);
    const completedScrollY = useSharedValue(0);
    const dataSections = useMemo(() => [
        {
            name: `Pending (${SIGNED_UP.data.length || 0})`,
            data: SIGNED_UP.data,
            status: 'SIGNED_UP',
            scrollY: pendingScrollY,
            total: SIGNED_UP.total,
        },
        {
            name: `Completed (${COMPLETED.data.length || 0})`,
            data: COMPLETED.data,
            status: 'COMPLETED',
            scrollY: completedScrollY,
            total: COMPLETED.total,
        },
    ], [
        SIGNED_UP.data,
        SIGNED_UP.total,
        pendingScrollY,
        COMPLETED.data,
        COMPLETED.total,
        completedScrollY,
    ]);
    // Each status list is one page of the horizontal scroll view. The frame
    // dimensions on web are based on the whole window, sidebar included, so the
    // page width comes from the measured container instead.
    const [pageWidth, setPageWidth] = useState(width + (paddingHorizontal || 0) * 2);
    const onLayout = useCallback((event) => {
        setPageWidth(event.nativeEvent.layout.width);
    }, []);
    const pagePadding = Math.max(0, (pageWidth - width) / 2);
    const sections = useMemo(() => {
        const itemWidth = width / 2 - rem(20);
        return dataSections.map((item) => ({
            ...item,
            id: item.name,
            text: item.name,
            width: itemWidth,
        }));
    }, [dataSections, width]);
    const scrollX = useSharedValue(0);
    const isUserScrolling = useSharedValue(false);
    const onScrollX = useAnimatedScrollHandler({
        onBeginDrag: () => {
            isUserScrolling.value = true;
        },
        onScroll: ({ contentOffset }) => {
            scrollX.value = contentOffset.x;
        },
        onMomentumEnd: () => {
            if (isUserScrolling.value) {
                runOnJS(triggerSelectorPress)();
            }
            isUserScrolling.value = false;
        },
    });
    const scrollY = useDerivedValue(() => {
        if (scrollX.value >= pageWidth) {
            return completedScrollY.value;
        }
        return pendingScrollY.value;
    }, [completedScrollY, pendingScrollY, pageWidth]);
    const horizontalScrollViewRef = useRef(null);
    const onSelectView = useCallback((index) => {
        triggerSelectorPress();
        horizontalScrollViewRef.current?.scrollTo({
            x: index * pageWidth,
            animated: true,
        });
    }, [horizontalScrollViewRef, pageWidth]);
    const paddingHorizontalStyle = useMemo(() => ({ paddingHorizontal: pagePadding }), [pagePadding]);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const isExtraMembersFeatureAvailable = useAppSelector(selectIsExtraMembersFeatureAvailable);
    const showExtraMembersCard = isExtraMembersFeatureAvailable && SIGNED_UP.total + COMPLETED.total > 0;
    return (<View style={styles.container} onLayout={onLayout}>
      <NavigationHeader scrollY={scrollY}>
        <View style={[
            styles.buttonContainer,
            { paddingHorizontal: pagePadding + rem(16) },
        ]}>
          <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={pageWidth} itemStyle={styles.itemStyle} selectorStyle={styles.selectorStyle} itemContainerStyle={styles.itemContainerStyle} spaceBetween={rem(8)} setSelection={onSelectView} withBackground/>
        </View>
      </NavigationHeader>
      <Animated.ScrollView ref={horizontalScrollViewRef} horizontal onScroll={onScrollX} pagingEnabled decelerationRate="fast" scrollEventThrottle={16} contentContainerStyle={{ width: dataSections.length * pageWidth }} showsHorizontalScrollIndicator={false}>
        {dataSections.map((s) => (<ReferralsStatusList key={s.status} {...s} width={pageWidth} currency={referrals.currency || defaultCurrency} contentContainerStyle={contentContainerStyle} showExtraMembersCard={showExtraMembersCard}/>))}
      </Animated.ScrollView>
    </View>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    titleContainer: {
        padding: rem(16),
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingHorizontal: rem(16),
        marginBottom: rem(16),
        marginTop: rem(8),
    },
    itemStyle: {
        borderRadius: rem(8),
        paddingVertical: rem(4),
    },
    itemContainerStyle: {
        padding: 0,
        margin: 0,
    },
    selectorStyle: {
        borderRadius: rem(8),
        justifyContent: 'center',
        padding: 0,
        margin: 0,
    },
}));
export default ReferralsList;
